import { type LucideIcon, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { VERB_COLOR, type IvrVerb } from '../types';

interface NodeCardProps {
  verb: IvrVerb;
  icon: LucideIcon;
  label: string;
  /** Optional one-line summary of the current config. */
  summary?: React.ReactNode;
  /** Whether the config gear shows. Hangup / Answer nodes don't configure. */
  configurable?: boolean;
  /** Removable shows the trash icon. Answer node is fixed. */
  removable?: boolean;
  onConfigure?: () => void;
  onRemove?: () => void;
  /** Slot under the summary, e.g. extra DTMF chips. */
  children?: React.ReactNode;
  /** Width override — DTMF nodes are taller to host per-digit handles. */
  className?: string;
  /**
   * "configured" → green dot under the icon, "incomplete" → amber.
   * Hides when undefined.
   */
  status?: 'configured' | 'incomplete';
  /** Drives the Brazilian-Coffee-style blue selection bar + ring. */
  selected?: boolean;
}

/**
 * Compact horizontal node card. ~half the previous footprint:
 *
 *   ┌──────────────────────────────┐
 *   │ ▌ ●  VERB · summary     ⚙ 🗑 │
 *   └──────────────────────────────┘
 *
 * Action buttons fade in on hover/selected to keep the footprint small at
 * rest. Verb stripe (▌) and ring switch to blue on selection.
 */
export function NodeCard({
  verb,
  icon: Icon,
  label,
  summary,
  configurable = true,
  removable = true,
  onConfigure,
  onRemove,
  children,
  className,
  status,
  selected = false,
}: NodeCardProps) {
  const v = VERB_COLOR[verb];
  return (
    <div
      className={cn(
        'group/node relative w-[120px] overflow-hidden rounded-md border bg-card shadow-sm transition-all hover:shadow-md',
        selected
          ? 'border-blue-400 ring-2 ring-blue-300/60 ring-offset-1'
          : 'border-border',
        className,
      )}
    >
      {/* Verb accent stripe — thickens + turns blue when selected. */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0',
          selected ? 'w-1 bg-blue-500' : 'w-[3px]',
          !selected && v.bg,
        )}
      />

      <div className="flex items-center gap-1 pl-1.5 pr-0.5 py-1">
        {/* Icon + status dot */}
        <div className="relative shrink-0">
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full text-white shadow-sm',
              v.bg,
            )}
          >
            <Icon className="h-2.5 w-2.5" />
          </div>
          {status && (
            <span
              aria-label={status === 'configured' ? 'Configured' : 'Needs config'}
              className={cn(
                'absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-card',
                status === 'configured' ? 'bg-emerald-500' : 'bg-amber-500',
              )}
            />
          )}
        </div>

        {/* Label + summary */}
        <div className="min-w-0 flex-1">
          <div className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground leading-none">
            {label}
          </div>
          {summary !== undefined && (
            <div className="mt-0.5 truncate text-[9px] leading-tight text-foreground">
              {summary}
            </div>
          )}
          {children}
        </div>

        {/* Actions — fade in on hover or selection. */}
        {(configurable || removable) && (
          <div
            className={cn(
              'flex flex-col -space-y-1 transition-opacity',
              selected
                ? 'opacity-100'
                : 'opacity-0 group-hover/node:opacity-100',
            )}
          >
            {configurable && onConfigure && (
              <Button
                size="icon"
                variant="ghost"
                className="h-4 w-4"
                onClick={onConfigure}
                aria-label="Configure"
              >
                <Settings className="h-2.5 w-2.5" />
              </Button>
            )}
            {removable && onRemove && (
              <Button
                size="icon"
                variant="ghost"
                className="h-4 w-4 text-destructive hover:text-destructive"
                onClick={onRemove}
                aria-label="Remove"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
